/**
 * Otokoç widget kit — product grid showcase, detail panel, video gallery, specs.
 *
 * Enhances the embedded chat frontend's product UI with:
 * • Custom card layout with spec chips (fuel, transmission, body type)
 * • Professional detail panel with hero image and full-width specs grid
 * • YouTube / native video gallery section
 * • "Test Sürüşü Al" / "Bilgi Al" CTAs on detail pages
 *
 * Also exports createOtokocRenderer() for the renderUISpec callback.
 */

import { queryAllAcrossRoots } from './domUtils.js';

// ── Helpers ──────────────────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function toUrlList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry): entry is string => typeof entry === 'string' && /^https?:\/\//.test(entry))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function readFirstString(candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return '';
}

function prettifyAttributeKey(key: string): string {
  const cleaned = key
    .replace(/^spec_/, '')
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .trim();
  if (!cleaned) return 'Teknik Detay';
  return cleaned
    .split(/\s+/)
    .map((part) => part.charAt(0).toLocaleUpperCase('tr-TR') + part.slice(1))
    .join(' ');
}

// ── Video helpers ────────────────────────────────────────────────────────

function resolveProductVideoUrls(product: Record<string, unknown>): string[] {
  const directVideos = toUrlList(product.media_videos);
  const extras = asRecord(product.extras);
  const extrasVideos = toUrlList(extras?.media_videos);
  return [...new Set([...directVideos, ...extrasVideos])];
}

function normalizeVideoEmbedUrl(url: string): string {
  if (/youtube\.com\/watch\?v=/.test(url)) {
    try {
      const parsed = new URL(url);
      const videoId = parsed.searchParams.get('v');
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return url;
    }
  }

  if (/youtu\.be\//.test(url)) {
    try {
      const parsed = new URL(url);
      const videoId = parsed.pathname.split('/').filter(Boolean)[0];
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return url;
    }
  }

  return url;
}

function isEmbedVideoUrl(url: string): boolean {
  return /youtube\.com\/embed\//.test(url);
}

// ── Shared ───────────────────────────────────────────────────────────────

function stripUnsupportedProductGridControls(container: ParentNode): void {
  for (const dropdown of Array.from(
    container.querySelectorAll('.gengage-chat-product-sort-dropdown')
  )) {
    dropdown.remove();
  }

  for (const toolbar of Array.from(
    container.querySelectorAll('.gengage-chat-product-sort-toolbar')
  )) {
    if (toolbar.childElementCount === 0) {
      toolbar.remove();
    }
  }

  for (const actions of Array.from(
    container.querySelectorAll('.gengage-chat-product-grid-head-actions')
  )) {
    if (actions.childElementCount === 0) {
      actions.remove();
    }
  }
}

function relabelSimilarSections(container: HTMLElement): void {
  for (const heading of Array.from(
    container.querySelectorAll(
      'h2, h3, h4, .gengage-chat-section-title, .gengage-chat-product-grid-head-title'
    )
  )) {
    if (!(heading instanceof HTMLElement)) continue;
    if ((heading.textContent || '').trim() === 'Benzer Ürünler') {
      heading.textContent = 'Benzer Araçlar';
    }
  }
}

// ── UISpec resolvers ─────────────────────────────────────────────────────

function resolveProductDetailsPayload(spec: {
  root: string;
  elements: Record<string, { type?: string; props?: Record<string, unknown> }>;
}): Record<string, unknown> | null {
  const rootElement = spec.elements?.[spec.root];
  if (!rootElement || rootElement.type !== 'ProductDetailsPanel') {
    return null;
  }

  const rootProps = asRecord(rootElement.props);
  if (!rootProps) {
    return null;
  }

  return asRecord(rootProps.product ?? rootProps);
}

function resolveProductGridProductMap(spec: {
  root: string;
  elements: Record<string, { type?: string; props?: Record<string, unknown> }>;
}): Map<string, Record<string, unknown>> {
  const productMap = new Map<string, Record<string, unknown>>();
  const elements = Object.values(spec.elements || {});

  for (const element of elements) {
    if (element.type !== 'ProductGrid') continue;

    const rootProps = asRecord(element.props);
    const products = Array.isArray(rootProps?.products) ? rootProps.products : [];
    for (const entry of products) {
      const product = asRecord(entry);
      if (!product) continue;
      const sku = readFirstString([product.sku]);
      if (!sku) continue;
      productMap.set(sku, product);
    }
  }

  for (const element of elements) {
    if (element.type !== 'ProductCard') continue;
    const rootProps = asRecord(element.props);
    const product = asRecord(rootProps?.product ?? rootProps);
    if (!product) continue;
    const sku = readFirstString([product.sku]);
    if (!sku) continue;
    productMap.set(sku, product);
  }

  return productMap;
}

// ── Product grid showcase ────────────────────────────────────────────────

function applyProductGridShowcase(
  container: HTMLElement,
  productMap: Map<string, Record<string, unknown>>
): void {
  const desktop = window.innerWidth >= 1024;
  stripUnsupportedProductGridControls(container);

  for (const grid of Array.from(container.querySelectorAll('.gengage-chat-product-grid'))) {
    if (!(grid instanceof HTMLElement)) continue;
    grid.style.setProperty(
      'grid-template-columns',
      desktop ? 'repeat(2, minmax(0, 1fr))' : '1fr',
      'important'
    );
    grid.style.setProperty('gap', desktop ? '20px' : '14px', 'important');
  }

  for (const card of Array.from(
    container.querySelectorAll('.gengage-chat-product-card[data-sku]')
  )) {
    if (!(card instanceof HTMLElement)) continue;

    const sku = card.getAttribute('data-sku') || '';
    const product = productMap.get(sku);

    card.style.setProperty('border-radius', '20px', 'important');
    card.style.setProperty('border', '1px solid #d6dde8', 'important');
    card.style.setProperty('box-shadow', '0 8px 24px rgba(16, 42, 67, 0.08)', 'important');
    card.style.setProperty('overflow', 'hidden', 'important');

    const imageWrap = card.querySelector('.gengage-chat-product-card-img-wrapper');
    if (imageWrap instanceof HTMLElement) {
      imageWrap.style.setProperty('aspect-ratio', '16 / 9', 'important');
      imageWrap.style.removeProperty('height');
      imageWrap.style.setProperty('min-height', '180px', 'important');
      imageWrap.style.setProperty('padding', '8px', 'important');
      imageWrap.style.setProperty(
        'background',
        'linear-gradient(180deg, #f3f6fb 0%, #eef2f8 100%)',
        'important'
      );
    }

    const image = card.querySelector('.gengage-chat-product-card-img');
    if (image instanceof HTMLImageElement) {
      image.style.setProperty('width', '100%', 'important');
      image.style.setProperty('height', 'auto', 'important');
      image.style.setProperty('max-height', '100%', 'important');
      image.style.setProperty('display', 'block', 'important');
      image.style.setProperty('object-fit', 'contain', 'important');
      image.style.setProperty('object-position', 'center center', 'important');
    }

    const cta = card.querySelector('.gengage-chat-product-card-cta');
    if (cta instanceof HTMLElement) {
      cta.style.setProperty('text-decoration', 'none', 'important');
      cta.style.setProperty('font-weight', '800', 'important');
      cta.style.setProperty('border-radius', '12px', 'important');
    }

    const body = card.querySelector('.gengage-chat-product-card-body');
    if (!(body instanceof HTMLElement) || !product) continue;

    if (body.querySelector('[data-otokoc-part="card-facts"]')) continue;

    const facetHits = asRecord(product.facet_hits) || {};
    const attributes = asRecord(product.attributes) || {};

    const fuel = readFirstString([facetHits['Yakıt Tipi'], attributes.fuel_type]);
    const transmission = readFirstString([facetHits['Vites'], attributes.transmission]);
    const bodyType = readFirstString([facetHits['Kasa Tipi'], attributes.body_type]);
    const dimensions = readFirstString([attributes.dimensions]);

    const chips = [fuel, transmission, bodyType].filter(Boolean).slice(0, 3);
    if (!chips.length && !dimensions) continue;

    const facts = document.createElement('div');
    facts.dataset.otokocPart = 'card-facts';
    facts.style.marginTop = '10px';

    if (chips.length) {
      const chipRow = document.createElement('div');
      chipRow.style.display = 'flex';
      chipRow.style.flexWrap = 'wrap';
      chipRow.style.gap = '6px';

      for (const chip of chips) {
        const chipEl = document.createElement('span');
        chipEl.textContent = chip;
        Object.assign(chipEl.style, {
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '999px',
          border: '1px solid rgba(15, 43, 91, 0.18)',
          background: '#f7f9fc',
          color: '#2a4365',
          fontSize: '11px',
          fontWeight: '700',
          padding: '4px 8px',
        });
        chipRow.appendChild(chipEl);
      }

      facts.appendChild(chipRow);
    }

    if (dimensions) {
      const dims = document.createElement('p');
      dims.textContent = `Ölçüler: ${dimensions}`;
      Object.assign(dims.style, {
        margin: '7px 0 0',
        color: '#4a607b',
        fontSize: '11px',
        lineHeight: '1.4',
      });
      facts.appendChild(dims);
    }

    body.appendChild(facts);
  }
}

// ── Detail specs ─────────────────────────────────────────────────────────

function appendProfessionalDetailSpecs(
  detailContent: HTMLElement,
  product: Record<string, unknown>
): void {
  if (detailContent.querySelector('[data-otokoc-part="detail-specs"]')) {
    return;
  }

  const attributes = asRecord(product.attributes) || {};
  const attributeLabels = asRecord(product.attribute_labels) || {};
  const facetHits = asRecord(product.facet_hits) || {};
  const knownEntries: Array<{ key: string; label: string; value: string }> = [
    {
      key: 'fuel_type',
      label: 'Yakıt Tipi',
      value: readFirstString([facetHits['Yakıt Tipi'], attributes.fuel_type]),
    },
    {
      key: 'transmission',
      label: 'Vites',
      value: readFirstString([facetHits['Vites'], attributes.transmission]),
    },
    {
      key: 'body_type',
      label: 'Kasa Tipi',
      value: readFirstString([facetHits['Kasa Tipi'], attributes.body_type]),
    },
    { key: 'engine', label: 'Motor', value: readFirstString([attributes.engine]) },
    {
      key: 'horsepower',
      label: 'Güç',
      value: readFirstString([attributes.horsepower ? `${attributes.horsepower} hp` : '']),
    },
    { key: 'torque', label: 'Tork', value: readFirstString([attributes.torque]) },
    { key: 'drivetrain', label: 'Çekiş', value: readFirstString([attributes.drivetrain]) },
    { key: 'dimensions', label: 'Ölçüler', value: readFirstString([attributes.dimensions]) },
    { key: 'trunk_volume', label: 'Bagaj', value: readFirstString([attributes.trunk_volume]) },
    { key: 'max_speed', label: 'Maksimum Hız', value: readFirstString([attributes.max_speed]) },
    {
      key: 'acceleration_0_100',
      label: '0-100',
      value: readFirstString([attributes.acceleration_0_100]),
    },
    {
      key: 'avg_fuel_consumption',
      label: 'Ortalama Tüketim',
      value: readFirstString([attributes.avg_fuel_consumption]),
    },
  ].filter((entry) => Boolean(entry.value));

  const knownKeys = new Set(knownEntries.map((entry) => entry.key));
  const extraEntries: Array<{ label: string; value: string }> = [];
  for (const [key, rawValue] of Object.entries(attributes)) {
    if (knownKeys.has(key)) continue;
    if (rawValue == null) continue;
    const value = typeof rawValue === 'string' ? rawValue.trim() : String(rawValue).trim();
    if (!value) continue;

    const mappedLabel =
      typeof attributeLabels[key] === 'string' ? String(attributeLabels[key]).trim() : '';
    extraEntries.push({
      label: mappedLabel || prettifyAttributeKey(key),
      value,
    });
  }

  const dedupedEntries = [
    ...knownEntries.map(({ label, value }) => ({ label, value })),
    ...extraEntries,
  ]
    .filter((entry, index, all) => {
      const signature = `${entry.label.toLowerCase()}::${entry.value.toLowerCase()}`;
      return (
        all.findIndex(
          (candidate) =>
            `${candidate.label.toLowerCase()}::${candidate.value.toLowerCase()}` === signature
        ) === index
      );
    })
    .slice(0, 16);

  if (!dedupedEntries.length) {
    return;
  }

  const section = document.createElement('section');
  section.dataset.otokocPart = 'detail-specs';
  Object.assign(section.style, {
    marginTop: '14px',
    border: '1px solid rgba(15, 43, 91, 0.12)',
    borderRadius: '16px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f7fafe 100%)',
    padding: '14px',
  });

  const heading = document.createElement('h4');
  heading.textContent =
    dedupedEntries.length > knownEntries.length
      ? 'Teknik Özellikler ve Detaylar'
      : 'Teknik Özellikler';
  Object.assign(heading.style, {
    margin: '0 0 10px',
    color: '#0f2b5b',
    fontSize: '14px',
    fontWeight: '800',
    letterSpacing: '0.01em',
  });
  section.appendChild(heading);

  const grid = document.createElement('div');
  Object.assign(grid.style, {
    display: 'grid',
    gridTemplateColumns: window.innerWidth >= 860 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
    gap: '8px',
  });

  for (const entry of dedupedEntries) {
    const row = document.createElement('div');
    Object.assign(row.style, {
      border: '1px solid rgba(15, 43, 91, 0.1)',
      borderRadius: '12px',
      padding: '8px 10px',
      background: '#fff',
    });

    const label = document.createElement('div');
    label.textContent = entry.label;
    Object.assign(label.style, {
      color: '#4a607b',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      marginBottom: '4px',
    });

    const value = document.createElement('div');
    value.textContent = entry.value;
    Object.assign(value.style, {
      color: '#102a43',
      fontSize: '13px',
      fontWeight: '700',
      lineHeight: '1.4',
    });

    row.appendChild(label);
    row.appendChild(value);
    grid.appendChild(row);
  }

  section.appendChild(grid);
  detailContent.appendChild(section);
}

// ── Detail CTAs ──────────────────────────────────────────────────────────

function appendProfessionalDetailCtas(
  detailContent: HTMLElement,
  product: Record<string, unknown>,
  context: {
    onAction: (action: { title: string; type: string; payload?: Record<string, unknown> }) => void;
  }
): void {
  if (detailContent.querySelector('[data-otokoc-part="detail-ctas"]')) {
    return;
  }

  const name = readFirstString([product.name]) || 'Bu araç';
  const legacyActions = detailContent.querySelector('.gengage-chat-product-details-actions');
  if (legacyActions instanceof HTMLElement) {
    legacyActions.style.display = 'none';
  }

  const section = document.createElement('section');
  section.dataset.otokocPart = 'detail-ctas';
  Object.assign(section.style, {
    marginTop: '14px',
    display: 'grid',
    gap: '8px',
    width: '100%',
    boxSizing: 'border-box',
  });

  const primaryRow = document.createElement('div');
  Object.assign(primaryRow.style, {
    display: 'grid',
    gap: '8px',
    gridTemplateColumns: window.innerWidth >= 860 ? '1fr 1fr' : '1fr',
    width: '100%',
    minWidth: '0',
  });

  const closePanelToMessagesOnMobile = () => {
    if (window.innerWidth > 860) {
      return;
    }

    const collapseNow = () => {
      const drawer = queryAllAcrossRoots('[data-gengage-part="chat-drawer"]')[0];
      if (drawer) {
        drawer.classList.remove('gengage-chat-drawer--with-panel');
      }

      const panel = queryAllAcrossRoots('[data-gengage-part="chat-panel"]')[0];
      if (panel) {
        panel.classList.remove('gengage-chat-panel--visible');
        panel.classList.remove('gengage-chat-panel--has-scroll');
        panel.style.setProperty('display', 'none', 'important');
      }

      const scroll = queryAllAcrossRoots('#gengage-chat-scroll')[0];
      if (scroll) {
        scroll.scrollTo({ top: scroll.scrollHeight, behavior: 'smooth' });
      }
    };

    let attempts = 0;
    const runCollapseSweep = () => {
      collapseNow();
      attempts += 1;
      if (attempts < 10) {
        window.setTimeout(runCollapseSweep, 180);
      }
    };

    window.setTimeout(runCollapseSweep, 120);
    window.setTimeout(collapseNow, 1300);
  };

  const createPrimary = (label: string, message: string, dark = true) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    Object.assign(button.style, {
      minHeight: '46px',
      width: '100%',
      minWidth: '0',
      borderRadius: '12px',
      border: dark ? '0' : '1px solid rgba(15, 43, 91, 0.2)',
      background: dark ? '#0f2b5b' : '#f6f9ff',
      color: dark ? '#fff' : '#0f2b5b',
      fontSize: '14px',
      fontWeight: '800',
      cursor: 'pointer',
    });
    button.addEventListener('click', () => {
      context.onAction({
        title: label,
        type: 'inputText',
        payload: { text: message },
      });
      closePanelToMessagesOnMobile();
    });
    return button;
  };

  primaryRow.appendChild(
    createPrimary('Test Sürüşü Al', `${name} için test sürüşü almak istiyorum`, true)
  );
  primaryRow.appendChild(
    createPrimary(
      'Bilgi Al',
      `${name} hakkında detaylı bilgi almak istiyorum. Otokoç benimle iletişime geçsin.`,
      false
    )
  );
  section.appendChild(primaryRow);

  const headingEl = detailContent.querySelector('h1, h2, h3');
  if (headingEl instanceof HTMLElement) {
    headingEl.insertAdjacentElement('afterend', section);
    return;
  }

  detailContent.appendChild(section);
}

// ── Video showcase ───────────────────────────────────────────────────────

function appendProductVideoShowcase(
  container: HTMLElement,
  product: Record<string, unknown>,
  productName: string
): void {
  if (container.querySelector('[data-otokoc-part="product-video-showcase"]')) {
    return;
  }

  const videoUrls = resolveProductVideoUrls(product).map(normalizeVideoEmbedUrl);
  if (videoUrls.length === 0) {
    return;
  }

  const detailContent = container.querySelector('.gengage-chat-product-details-content');
  if (!detailContent) {
    return;
  }

  const section = document.createElement('section');
  section.className = 'otokoc-product-video-showcase';
  section.dataset.otokocPart = 'product-video-showcase';
  Object.assign(section.style, {
    marginTop: '18px',
    border: '1px solid rgba(15, 43, 91, 0.14)',
    borderRadius: '14px',
    background: 'linear-gradient(165deg, rgba(15, 43, 91, 0.03), rgba(255, 255, 255, 0.96))',
    padding: '12px',
  });

  const headingEl = document.createElement('h4');
  headingEl.className = 'otokoc-product-video-showcase-title';
  headingEl.textContent = `${productName} Video Galerisi`;
  Object.assign(headingEl.style, {
    margin: '0',
    color: '#0f2b5b',
    fontSize: '14px',
    fontWeight: '700',
  });
  section.appendChild(headingEl);

  const subtitle = document.createElement('p');
  subtitle.className = 'otokoc-product-video-showcase-subtitle';
  subtitle.textContent = 'Resmi model tanıtım videoları';
  Object.assign(subtitle.style, {
    margin: '4px 0 10px',
    color: '#3c4f6e',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  });
  section.appendChild(subtitle);

  const grid = document.createElement('div');
  grid.className = 'otokoc-product-video-grid';
  Object.assign(grid.style, {
    display: 'grid',
    gap: '8px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  });

  for (const url of videoUrls) {
    if (isEmbedVideoUrl(url)) {
      const frame = document.createElement('iframe');
      frame.className = 'otokoc-product-video-frame';
      frame.src = url;
      frame.title = `${productName} videosu`;
      frame.loading = 'lazy';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      Object.assign(frame.style, {
        width: '100%',
        borderRadius: '10px',
        border: '1px solid rgba(15, 43, 91, 0.18)',
        background: '#060d1a',
        aspectRatio: '16 / 9',
      });
      grid.appendChild(frame);
      continue;
    }

    const video = document.createElement('video');
    video.className = 'otokoc-product-video';
    video.controls = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = url;
    Object.assign(video.style, {
      width: '100%',
      borderRadius: '10px',
      border: '1px solid rgba(15, 43, 91, 0.18)',
      background: '#060d1a',
      aspectRatio: '16 / 9',
    });
    grid.appendChild(video);
  }

  section.appendChild(grid);

  const actions = detailContent.querySelector('.gengage-chat-product-details-actions');
  if (actions) {
    detailContent.insertBefore(section, actions);
    return;
  }
  detailContent.appendChild(section);
}

// ── Professional detail panel ────────────────────────────────────────────

function applyProfessionalDetailShowcase(
  container: HTMLElement,
  product: Record<string, unknown>,
  context: {
    onAction: (action: { title: string; type: string; payload?: Record<string, unknown> }) => void;
  }
): void {
  const desktop = window.innerWidth >= 960;
  const panel = container.querySelector('.gengage-chat-product-details-panel');
  if (!(panel instanceof HTMLElement)) {
    return;
  }

  stripUnsupportedProductGridControls(container);
  panel.style.removeProperty('display');
  Object.assign(panel.style, {
    display: 'flex',
    flexDirection: 'column',
    gridTemplateColumns: '',
    border: '1px solid rgba(15, 43, 91, 0.12)',
    borderRadius: '22px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
    overflow: 'hidden',
    alignItems: 'stretch',
    width: '100%',
    maxWidth: desktop ? '1180px' : '100%',
    margin: desktop ? '0 auto' : '',
    gap: '0',
    padding: '0',
  });

  for (const media of Array.from(panel.querySelectorAll('.gengage-chat-product-details-media'))) {
    if (!(media instanceof HTMLElement)) continue;
    Object.assign(media.style, {
      padding: '0',
      border: '0',
      background: 'transparent',
      alignItems: 'stretch',
    });
  }

  for (const mediaWrap of Array.from(
    panel.querySelectorAll('.gengage-chat-product-details-img-wrap')
  )) {
    if (!(mediaWrap instanceof HTMLElement)) continue;
    Object.assign(mediaWrap.style, {
      padding: '0',
      background: 'linear-gradient(180deg, #f3f6fb 0%, #eef2f8 100%)',
      position: 'relative',
      top: '',
      alignSelf: 'stretch',
      width: '100%',
    });
  }

  for (const image of Array.from(panel.querySelectorAll('.gengage-chat-product-details-img'))) {
    if (!(image instanceof HTMLImageElement)) continue;
    image.style.objectFit = 'contain';
    image.style.objectPosition = 'center center';
    image.style.maxHeight = desktop ? '640px' : '420px';
    image.style.borderRadius = '0';
  }

  const detailContent = panel.querySelector('.gengage-chat-product-details-content');
  if (!(detailContent instanceof HTMLElement)) {
    return;
  }

  Object.assign(detailContent.style, {
    padding: desktop ? '18px' : '14px',
    gap: '12px',
    width: '100%',
    boxSizing: 'border-box',
  });

  appendProfessionalDetailSpecs(detailContent, product);
  appendProfessionalDetailCtas(detailContent, product, context);
  relabelSimilarSections(container);
}

// ── Renderer factory ─────────────────────────────────────────────────────

/**
 * Creates the renderUISpec callback to pass as `chat.renderer.renderUISpec`
 * in the initOverlayWidgets options.
 */
export function createOtokocRenderer(): {
  renderUISpec: (
    spec: {
      root: string;
      elements: Record<string, { type?: string; props?: Record<string, unknown> }>;
    },
    context: {
      onAction: (action: {
        title: string;
        type: string;
        payload?: Record<string, unknown>;
      }) => void;
    },
    helpers: { defaultRender: (spec: unknown, context: unknown) => HTMLElement }
  ) => HTMLElement;
} {
  return {
    renderUISpec: (spec, context, helpers) => {
      const rendered = helpers.defaultRender(spec, context);
      const productMap = resolveProductGridProductMap(spec);
      if (productMap.size > 0) {
        applyProductGridShowcase(rendered, productMap);
      }

      const product = resolveProductDetailsPayload(spec);
      if (!product) {
        relabelSimilarSections(rendered);
        return rendered;
      }

      applyProfessionalDetailShowcase(rendered, product, context);
      appendProductVideoShowcase(rendered, product, String(product.name ?? 'Araç'));
      return rendered;
    },
  };
}
